import { Icon, IconDisplay, IconSize } from "@components/Icon";
import { css } from "@emotion/react";
import type { CSSInterpolation } from "@emotion/serialize";
import React from "react";

import { colorPalette } from "@every.org/common/src/display/palette";

interface VideoEmbedProps {
  videoUrl: string;
  /**
   * Whether to wrap the video in a Card component with rounded corners.
   * Defaults to true.
   */
  wrapInCard?: boolean;
  /**
   * Border radius for the card wrapper. Only applies when wrapInCard is true.
   * Defaults to "16px 16px 0 0" (rounded top corners)
   */
  borderRadius?: string;
  /**
   * Custom CSS for the iframe container
   */
  containerCss?: CSSInterpolation;
  /**
   * Title for the iframe (for accessibility)
   */
  title?: string;
  /**
   * Whether to show a preview thumbnail that needs to be clicked to play the video.
   * When true, displays a thumbnail with a play button overlay.
   */
  isPreview?: boolean;
  /** Visual style for the play button centered over a video preview. */
  previewPlayButtonStyle?: VideoEmbedPreviewPlayButtonStyle;
  /** Whether the preview should fill a parent with an explicit size. */
  previewFillsContainer?: boolean;
  /**
   * Whether to automatically play the video inline when the preview is clicked.
   * Only applies when isPreview is true.
   * Defaults to false.
   */
  autoplay?: boolean;
  /**
   * Optional callback function that is called when the preview play button is clicked.
   * This is called regardless of the autoplay setting.
   * The parent component can control what happens (e.g., open a modal, track analytics, etc.)
   */
  onPreviewClick?: () => void;
}

export enum VideoEmbedPreviewPlayButtonStyle {
  DEFAULT = "DEFAULT",
  YOUTUBE = "YOUTUBE",
}

const iframeContainerCss = css`
  position: relative;
  overflow: hidden;
`;

const iframeCss = css`
  width: 100%;
  height: 100%;
`;

const previewContainerCss = css`
  position: relative;
  /* Defaulting to 16:9 ratio */
  padding: 0 0 56.25% 0;
  overflow: hidden;
  cursor: pointer;
  background-color: ${colorPalette.grayDark};
  transition: all 0.2s ease;

  &:hover {
    filter: drop-shadow(0px 8px 16px rgba(46, 52, 52, 0.2));
  }
`;

const thumbnailCss = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const playButtonOverlayCss = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const youtubePlayButtonOverlayCss = css`
  width: 68px;
  height: 40px;
  background: #ff0000;
  border-radius: 12px;

  &::after {
    content: "";
    width: 0;
    height: 0;
    margin-left: 3px;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-left: 14px solid white;
  }
`;

/**
 * Extract video ID from Vimeo URL
 */
function getVimeoVideoId(url: string): string | null {
  const patterns = [/vimeo\.com\/(\d+)/, /player\.vimeo\.com\/video\/(\d+)/];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
}

/**
 * Convert regular Vimeo URL to player URL
 */
function convertToVimeoPlayerUrl(url: string): string {
  // If already a player URL, return as is
  if (url.includes("player.vimeo.com")) {
    return url;
  }

  // Extract video ID from vimeo.com URL
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch && vimeoMatch[1]) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  return url;
}

/**
 * Extract video ID from YouTube URL
 */
function getYouTubeVideoId(url: string): string | null {
  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtube\.com\/embed\/([^?]+)/,
    /youtu\.be\/([^?]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
}

/**
 * Get thumbnail URL for a video
 */
function getVideoThumbnail(videoUrl: string): string | null {
  const vimeoId = getVimeoVideoId(videoUrl);
  if (vimeoId) {
    // Using a generic Vimeo thumbnail pattern
    return `https://vumbnail.com/${vimeoId}.jpg`;
  }

  const youtubeId = getYouTubeVideoId(videoUrl);
  if (youtubeId) {
    return `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;
  }

  return null;
}

/**
 * Component for embedding videos (YouTube, Vimeo, etc.) in an iframe
 * with a responsive 16:9 aspect ratio
 */
export const VideoEmbed: React.FCC<VideoEmbedProps> = ({
  videoUrl,
  borderRadius = "16px 16px 0 0",
  containerCss,
  title = "embedded-video",
  isPreview = false,
  previewPlayButtonStyle = VideoEmbedPreviewPlayButtonStyle.DEFAULT,
  previewFillsContainer = false,
  autoplay = false,
  onPreviewClick,
}) => {
  // Normalize Vimeo URLs to player URLs
  const normalizedUrl = convertToVimeoPlayerUrl(videoUrl);
  const thumbnailUrl = getVideoThumbnail(videoUrl);
  const youtubeId = getYouTubeVideoId(videoUrl);
  const youtubeFallbackThumbnailUrl = youtubeId
    ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
    : null;
  const iframeUrl = autoplay
    ? `${normalizedUrl}${normalizedUrl.includes("?") ? "&" : "?"}autoplay=1`
    : normalizedUrl;

  const handlePreviewClick = () => {
    if (onPreviewClick) {
      onPreviewClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handlePreviewClick();
    }
  };

  const handleYouTubeThumbnailFallback = (
    event: React.SyntheticEvent<HTMLImageElement>
  ) => {
    if (
      youtubeFallbackThumbnailUrl &&
      event.currentTarget.src !== youtubeFallbackThumbnailUrl &&
      event.currentTarget.naturalWidth <= 120
    ) {
      event.currentTarget.src = youtubeFallbackThumbnailUrl;
    }
  };

  const iframe = (
    <iframe
      title={title}
      css={iframeCss}
      id="smh_player"
      width="100%"
      height="100%"
      src={iframeUrl}
      allowFullScreen
      allow="autoplay *; fullscreen *; encrypted-media *"
      frameBorder="0"
    />
  );

  const preview = (
    <div
      css={[
        previewContainerCss,
        previewFillsContainer && { height: "100%", padding: 0 },
        { borderRadius },
      ]}
      onClick={handlePreviewClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label={title === "embedded-video" ? "Play video" : `Play ${title}`}
    >
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt="Video thumbnail"
          css={thumbnailCss}
          onError={handleYouTubeThumbnailFallback}
          onLoad={handleYouTubeThumbnailFallback}
        />
      )}
      <div
        css={[
          playButtonOverlayCss,
          previewPlayButtonStyle === VideoEmbedPreviewPlayButtonStyle.YOUTUBE &&
            youtubePlayButtonOverlayCss,
        ]}
      >
        {previewPlayButtonStyle === VideoEmbedPreviewPlayButtonStyle.DEFAULT ? (
          <Icon
            iconImport={() => import("@components/Icon/icons/PlayIcon")}
            size={IconSize.X_LARGE}
            display={IconDisplay.CURRENT_COLOR}
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <div css={[iframeContainerCss, { borderRadius }, containerCss]}>
      {isPreview ? preview : iframe}
    </div>
  );
};
